function atelierInit(host){
  "use strict";

  /* THE RANGE, AS LIQUID PRINTED IT.

     A JSON blob in its own script tag rather than values interpolated
     into this code. Product titles contain apostrophes, quotes and
     ampersands; a customer's quote can contain both kinds of quote at
     once. Interpolating any of that into JavaScript is a syntax error
     waiting for the wrong product name, and JSON.parse cannot be
     tricked by a title. */
  var DATA = JSON.parse(host.querySelector("[data-atelier-data]").textContent);
  /* The drawing names its own gradient and hatch. Deriving them
     from the host worked while there was exactly one drawing per
     section; the order sheet has two, so the drawing is the only
     thing that can answer which is which. */
  var _svg  = host.querySelector("svg.folio");
  var FOIL  = "url(#" + _svg.dataset.foil + ")";
  var HATCH = "url(#" + _svg.dataset.hatch + ")";

  // ---- real data, read from the store ----------------------------------
  var FOLIOS = DATA.folios;
  var LACES = DATA.laces;
  /* ==================================================================
     THE RANGE, READ FROM THE COLLECTIONS

     Nothing below is typed by hand any more. In the live section each of
     these arrays is printed by Liquid from a collection, so a charm or a
     title added to the shop appears in the atelier with no code change:

       {%- assign charms = collections['charms'] -%}
       var CHARMS = [
       {%- for p in charms.products -%}
         {%- assign v = p.selected_or_first_available_variant -%}
         { h: {{ p.handle | json }}, n: {{ p.title | json }},
           id: {{ v.id }}, price: {{ v.price | divided_by: 100.0 }},
           ok: {{ v.available | json }},
           img: {{ p.featured_image | image_url: width: 96 | json }} },
       {%- endfor -%}
       ];

     WHY THAT ALREADY MEANS "ACTIVE ONLY".

     `collection.products` in Liquid returns only products published to
     the Online Store. A draft is invisible there, and so is an unlisted
     one. There is no filter to write and no status to check — the
     atelier cannot show a charm you have not published, and it picks one
     up the moment you do. Worth being precise about, because ACTIVE and
     PUBLISHED are not the same thing: that is exactly what made the
     debossing product invisible until it was published to the channel.

     What that means for the store as it stands is printed under the
     picker, so it is visible rather than buried in here.
  ================================================================== */
  var CDN = DATA.cdn;
  var CHARMS = DATA.charms;

  /* What the Charms collection holds versus what the atelier can show.
     Printed under the picker so the gap is a fact on the page rather
     than something someone has to go and count. */
  var CHARM_SOURCE = DATA.charmSource;
  var EMBOSS = DATA.emboss;

  /* ------------------------------------------------------------------
     WHAT THE PRESS SAYS

     Two lengths, not two products. The press, the typeface and the
     finish are the same either way — a quote is simply a longer setting,
     and it is charged for because it takes longer to set and leaves less
     room for error, not because it is a different service.
  ------------------------------------------------------------------ */
  var PRESS = [
    {k:"monogram", n:"Monogram", max:3,  surcharge:0, hint:"MJH"},
    {k:"quote",    n:"Quote",    max:18, surcharge:5, hint:"BEGIN AGAIN"}
  ];

  /* The characters the press can set — the same string the off-the-shelf
     block uses, so the two cannot drift apart, plus a space, which a
     quote needs and three initials never do.

     One string doing two jobs: it is what gets printed for the customer
     AND what the field filters against, so there is one place to change
     the answer and no way for the list and the rule to disagree. */
  var CHARS = DATA.chars;

  /* ------------------------------------------------------------------
     WHERE THE PRESS GOES, AND WHICH WAY IT READS

     The folio holds A5 notebooks, so it is used standing portrait. The
     monogram is most often pressed to read with the folio carried flat
     under the arm, like a portfolio — so a placement has two facts, not
     one: where on the cover, and which way up it reads.

     `at` is [across, down] as a fraction of the cover, given in the
     frame the placement is NAMED in. "Lower right" means lower right of
     the folio as you carry it, which for a landscape-reading press is
     the landscape view.

     Add, remove or rename freely — the select, the diagram and the cart
     properties all read this one list.
  ------------------------------------------------------------------ */
  var PLACEMENTS = [
    {n:"Lower right",   reads:"landscape", at:[.88, .74], room:false},
    {n:"Lower centre",  reads:"landscape", at:[.50, .74], room:true},
    {n:"Upper right",   reads:"landscape", at:[.88, .20], room:false},
    {n:"Centre",        reads:"landscape", at:[.50, .50], room:true},
    {n:"Front, upright",reads:"portrait",  at:[.50, .82], room:true},
    {n:"Spine",         reads:"portrait",  at:[.12, .82], room:false}
  ];

  /* `room` is whether a LONG press fits there. Eighteen characters
     centred on a corner would hang off the edge of the folio, so the
     corners and the spine take a monogram only and the select offers
     just the roomy ones while a quote is chosen.

     This is a judgement about the object, not a technical limit — if the
     bench can set a quote down the spine, flip the flag and it appears.
     What must not happen is the diagram drawing a quote in a corner it
     could never actually be pressed into. */

  /* Which way the folio turns when it is carried flat. Rotating it
     clockwise puts the spine edge along the top.

     This is the ONE assumption in the drawing that a photograph would
     settle: a monogrammed folio stood upright, so we can see which way
     the initials lie against the spine. It only affects the portrait
     view of a landscape-reading press — the carried view, which is the
     one the customer is shown by default, is unaffected either way. */
  var CARRY_TURN = -90;   // degrees applied to the folio group
  /* ------------------------------------------------------------------
     COVER COLOURS

     `c` on each book below is the product's own `custom.colour` field,
     read from the store exactly as it stands today. It holds a NAME, not
     a colour, so the atelier needs one dictionary turning names into
     something it can draw. In the live build that dictionary is a shop
     metafield — same pattern as the monogram wording — so a new colour
     is one line for the team rather than a code change.

     What the store actually contains today is in the note under the
     diagram, and it is not clean: four of the titles below have no
     colour recorded at all, and Future Planner in Thyme is recorded as
     Blushing Oyster Gray. A notebook with no colour draws hatched rather
     than guessing, so the gaps are visible instead of quietly wrong.
  ------------------------------------------------------------------ */
  var COLOURS = DATA.colours;

  /* ------------------------------------------------------------------
     THE INSERTS, BY CATEGORY

     Read from the three range collections rather than from one flat
     list, because the categories ARE those collections — there is no
     separate field to keep in step, and a title moved between ranges in
     the admin moves in the atelier at the same moment.

       {%- assign cats = 'planners,hobby-notes,classics-range' | split: ',' -%}
       {%- for handle in cats -%}
         {%- for p in collections[handle].products -%}
           {%- assign v = p.selected_or_first_available_variant -%}
           { h: {{ p.handle | json }}, t: {{ p.title | json }},
             cat: {{ handle | json }},
             id: {{ v.id }}, p: {{ v.price | divided_by: 100.0 }},
             ok: {{ v.available | json }},
             c: {{ p.metafields.custom.colour.value | json }} },
         {%- endfor -%}
       {%- endfor -%}

     Same rule as the charms: `collection.products` returns only what is
     published, so drafts stay invisible and appear the moment they go
     live. Every published title lands in exactly one of the three.

     READING THESE RATHER THAN THE NOTEBOOK COLLECTION FOUND FIVE TITLES
     that were live and orderable but missing from it: Bullet Note,
     Writing Friend, Weekly Rituals, and both Weekly Dashboards. They are
     in the list below because the ranges know about them.

     It also brings in five multi-notebook bundles, which are not
     inserts — four Fast Brain Friend Kits and the Complete Set. The kits
     are excluded because they sit in a kits collection; the Complete Set
     sits in none, so it is named in BUNDLES until it does.
  ------------------------------------------------------------------ */
  var CATEGORIES = DATA.categories;

  function resolveColours(list){
    var missing = [];
    list.forEach(function(x){
      x.hex = COLOURS[x.n] || null;
      if (!x.hex) missing.push(x.n);
    });
    return missing;
  }
  var NO_COLOUR = resolveColours(FOLIOS).concat(resolveColours(LACES));
  if (NO_COLOUR.length && window.console)
    console.warn("atelier: no colour recorded in the shop setting for " +
                 NO_COLOUR.join(", "));

  /* Bundles wear no flag of their own. In the live section this is
       {%- unless p.collections contains kits_collection -%}
     which catches four of the five. The Complete Set belongs to no kits
     collection, so until someone adds it to one it has to be named. One
     line of merchandising removes this list entirely. */
  // bundles are filtered out in Liquid now, before they ever reach here

  var BOOKS = DATA.books;

  var BOOK_SOURCE = DATA.bookSource;

  function book(title){
    for (var i = 0; i < BOOKS.length; i++) if (BOOKS[i].t === title) return BOOKS[i];
    return null;
  }
  function coverHex(b){
    if (!b) return null;
    if (b.hex) return b.hex;               // set on the product itself
    return b.c ? (COLOURS[b.c] || null) : null;
  }

  /* The charm's own photograph, with its initial underneath it.

     The letter is not decoration: if the image has not loaded the tile
     would otherwise be a blank grey square, which is what a page opened
     from a file shows, and what a viewer behind a slow connection sees
     for the first second. A charm you cannot tell apart from the one
     next to it is worse than a plain label. */
  function swatchImg(c){
    return '<i style="background-image:url(' + c.img + ');background-size:cover;' +
           'background-position:center;display:grid;place-items:center;' +
           'font-size:9px;color:var(--soft)">' + c.n.charAt(0) + '</i>';
  }

  function charm(handle){
    for (var i = 0; i < CHARMS.length; i++) if (CHARMS[i].h === handle) return CHARMS[i];
    return null;
  }

  /* S.charms is a plain list of handles WITH REPEATS — three lunar
     charms is ["charms-lunar","charms-lunar","charms-lunar"]. It is the
     simplest thing that supports "as many as you like, including several
     of the same one", and it keeps the order they were added in, which
     is the order they hang.

     This collapses it to {charm, qty} for the places that want counting:
     the tally, the cart payload, the summary. The drawing wants the flat
     list, because five charms hang as five charms. */
  function charmCounts(){
    var order = [], by = {};
    S.charms.forEach(function(h){
      if (!by[h]){ by[h] = { charm: charm(h), qty: 0 }; order.push(by[h]); }
      by[h].qty++;
    });
    return order.filter(function(g){ return g.charm; });
  }
  /* S.books is now the same shape as S.charms: a flat list of titles
     WITH REPEATS. Four Ruled Notes is ["Ruled Note","Ruled Note",
     "Ruled Note","Ruled Note"].

     That choice is what makes the rest of this file barely change. The
     DRAWING already walks S.books by index and draws one slot each, so
     four of the same title draw as four notebooks without a line of new
     code. The CAP already reads S.books.length, which now means four
     notebooks rather than four titles — which is the honest reading:
     a folio holds four, whatever is printed on them.

     Only the places that COUNT need the collapsed form, and they are the
     same three the charms already needed it for: the tally, the spec and
     the cart. */
  function bookCounts(){
    var order = [], by = {};
    S.books.forEach(function(t){
      if (!by[t]){ by[t] = { title: t, book: book(t), qty: 0 }; order.push(by[t]); }
      by[t].qty++;
    });
    return order;
  }

  var MAX_INSERTS = DATA.maxInserts;   // charms carry their own price now

  // ---- state -----------------------------------------------------------
  var S = { folio:0, inner:0, outer1:0, outer2:null, emboss:0,
            initials:"", press:0, place:"Lower right", charms:[], books:[], cat:"",
            view:"auto" };   // auto | portrait | carried

  /* ---- the cover, in the folio's own coordinates ---------------------
     A5: 232 x 329 is 0.705, and the group's origin is its centre. Every
     position in the drawing is derived from these four numbers, so
     changing the cover size moves everything with it. */
    var COVER = { w:232, h:329, cx:200, cy:178 };

  /* ------------------------------------------------------------------
     TWO ARRANGEMENTS OF THE SAME DRAWING

     The folio is A5 — 232 wide to 329 tall. Stack the notebooks in a
     row underneath it and the whole picture is portrait, 400 x 496, and
     on a phone that gets capped at 34vh and letterboxed: the drawing
     came out 134px wide in a box 348px wide, with 200px of nothing
     either side of it. Tall picture, wide hole.

     Landscape puts the notebooks in a 2 x 2 block beside the folio
     instead of beneath it. Same height, and the folio renders half
     again as big, because the width was there all along.

     Nothing here is a second drawing. Both arrangements move the SAME
     elements: the cover follows COVER.cx/cy, and the charms and the
     monogram follow the cover through toView(), so they come along
     without being mentioned. Only four things need placing by hand.

     The order sheet renders this snippet too, and never calls
     applyLayout — it prints on A4, where portrait is right. The
     authored coordinates in the snippet ARE the portrait plan, so
     leaving it alone leaves it correct.
  ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     ONE BOX PER ARRANGEMENT, SIZED TO THE WORST VIEW IT HAS TO HOLD

     A first attempt sized the carried view's box to the carried view —
     tighter by a finger's width, and it clipped the charms, which hang
     BELOW the folio when it is lying flat. The saving was 44 units of
     496; the cost was a dangling charm cut in half. So each arrangement
     gets one box, big enough for the taller of its two views, and the
     turn is free.

     Landscape is 480 x 368 because turned flat the cover is 329 across
     rather than 232, and the belly laces hang 130 past it either side.
  ------------------------------------------------------------------ */
  var LAYOUTS = {
    portrait: {
      view:  "0 0 400 496",
      cover: { cx:200, cy:178 },
      label: { x:64,  y:386 },
      lace:  { x:200, y:396 },
      slots: { cx:200, y:398, perRow:4 },
      note:  { x:64,  y:488 }
    },
    landscape: {
      view:  "0 0 480 368",
      cover: { cx:174, cy:172 },
      label: { x:360, y:82  },
      lace:  { x:413, y:92  },
      slots: { cx:413, y:94, perRow:2 },
      note:  { x:10,  y:362 }
    }
  };
  var PLAN = LAYOUTS.portrait;

  function applyLayout(name){
    var next = LAYOUTS[name] || LAYOUTS.portrait;
    if (next === PLAN) return;
    PLAN = next;
    var svg = host.querySelector("svg.folio");
    if (svg) svg.setAttribute("viewBox", PLAN.view);
    COVER.cx = PLAN.cover.cx;
    COVER.cy = PLAN.cover.cy;
    var lab = $("insidelabel");
    if (lab){ lab.setAttribute("x", PLAN.label.x); lab.setAttribute("y", PLAN.label.y); }
    var note = $("slotnote");
    if (note){ note.setAttribute("x", PLAN.note.x); note.setAttribute("y", PLAN.note.y); }
    draw();
  }

  function placement(name){
    for (var i = 0; i < PLACEMENTS.length; i++)
      if (PLACEMENTS[i].n === name) return PLACEMENTS[i];
    return PLACEMENTS[0];
  }

  function press(){ return PRESS[S.press]; }
  function isQuote(){ return press().k === "quote"; }

  /* Where this press can go. A monogram goes anywhere; a quote only
     where there is room for eighteen characters. */
  function placesFor(){
    return PLACEMENTS.filter(function(p){ return !isQuote() || p.room; });
  }

  /* Only what the press can set, uppercased, and capped at the length
     this kind allows. A space is allowed for a quote and not for a
     monogram — three initials with a space in them is a typo, and
     silently keeping it would reach the bench as one. */
  function clean(v){
    var allow = CHARS + (isQuote() ? " " : "");
    var out = "";
    v.toUpperCase().split("").forEach(function(ch){
      if (allow.indexOf(ch) > -1) out += ch;
    });
    return out.slice(0, press().max);
  }

  /* The type size that keeps eighteen characters on the folio.

     The press is the same size whichever kind it is — that is the whole
     point of it being one press — so a quote is not shrunk to fit. What
     changes is where it is allowed to go, which is why PLACEMENTS carries
     `room`. This number is the drawn size for both. */
  /* THE DRAWN SIZE, TAKEN FROM THE REAL ONE.

     Measured off the periwinkle sample for the off-the-shelf block:
     three initials are 9.6% of the folio's long side and the cap height
     is 4.8% of its short side. On this cover — 232 x 329 — that is a
     17px setting in Times, which is what the `mono` text carries.

     It was 20px in Newsreader at weight 600 before, roughly a third too
     big and in the wrong typeface: the press sets Times, so the drawing
     should. The press is also ONE size — a quote is not shrunk to fit,
     it is broken onto a second line instead. */
  var PRESS_PX = 17, LINE_PX = 20;

  /* Break a quote across two lines at the space nearest the middle.

     Nearest the middle rather than the last space that fits, because
     this is a line pressed into leather, not a paragraph — two balanced
     lines look set, and "BEGIN AGAIN," over "SOFTLY" reads better than
     "BEGIN AGAIN, SOFT" over "LY". A run of eighteen characters with no
     space in it is one word and stays on one line. */
  function pressLines(str){
    if (!str) return [];
    if (str.length <= 10 || str.indexOf(" ") === -1) return [str];
    var mid = str.length / 2, best = -1;
    for (var i = 0; i < str.length; i++)
      if (str[i] === " " && (best === -1 || Math.abs(i - mid) < Math.abs(best - mid)))
        best = i;
    if (best === -1) return [str];
    return [str.slice(0, best), str.slice(best + 1)];
  }

  function setPressText(el, str){
    while (el.firstChild) el.removeChild(el.firstChild);
    var rows = pressLines(str);
    // lift the block so two lines straddle the placement point rather
    // than hanging below it
    var top = -(rows.length - 1) * LINE_PX / 2;
    rows.forEach(function(line, i){
      var t = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
      t.setAttribute("x", 0);
      t.setAttribute("dy", i === 0 ? top : LINE_PX);
      t.textContent = line;
      el.appendChild(t);
    });
  }

  /* Is the folio being shown carried flat? On `auto` that follows the
     placement, which is the whole point — choose a press that reads
     landscape and the folio turns so you can read it. */
  function carried(){
    if (S.view === "portrait") return false;
    if (S.view === "carried")  return true;
    return S.emboss > 0 && !!S.initials && placement(S.place).reads === "landscape";
  }

  /* A placement's [across, down] is given in the frame it is named in.
     This returns it in the folio's own portrait frame, so one number
     pair can be drawn either way up. */
  function localXY(p){
    var a = p.at[0], d = p.at[1];
    if (p.reads === "landscape"){
      /* Undo the carry turn: landscape (a,d) -> portrait (1-d, a).

         Worth deriving rather than guessing, because the mirror of this
         is also a rotation and looks entirely plausible on screen — it
         just puts "lower right" in the lower LEFT corner. The group is
         rotated by CARRY_TURN = -90, which maps a local point (x,y) to
         the view offset (y, -x); solving that for the landscape
         fractions gives across = 1 - d, down = a. */
      var t = 1 - d; d = a; a = t;
    }
    return { x:(a - .5) * COVER.w, y:(d - .5) * COVER.h };
  }

  /* Scoped to this section. Two atelier sections on one page would
     otherwise share every id between them and drive each other's
     drawing. */
  var $ = function(id){ return host.querySelector("[data-el=" + id + "]"); };
  var money = function(n){ return "$" + n.toFixed(2); };

  // ---- controls -------------------------------------------------------
  // Built ONCE. Rebuilding them on every change would throw away the
  // element the keyboard is focused on, so a keyboard user loses their
  // place the moment they choose anything. Redraw only sets aria-pressed.
  var groups = [];

  function swatches(host, data, get, set, allowNone){
    var buttons = [];
    if (allowNone){
      var none = document.createElement("button");
      none.type = "button"; none.className = "opt";
      none.textContent = "None";
      none.onclick = function(){ set(null); draw(); };
      host.appendChild(none);
      buttons.push({ el:none, val:null });
    }
    data.forEach(function(d, i){
      var b = document.createElement("button");
      b.type = "button"; b.className = "sw";
      b.style.setProperty("--dot", d.hex);
      var out = d.ok === false || d.stock < 0;
      if (out) b.disabled = true;
      b.innerHTML = '<i></i><span>' + d.n +
        (out ? '<small>unavailable</small>' : '') + '</span>';
      b.onclick = function(){ set(i); draw(); };
      host.appendChild(b);
      buttons.push({ el:b, val:i });
    });
    groups.push({ buttons:buttons, get:get });
  }

  function options(host, data, get, set){
    var buttons = [];
    data.forEach(function(d, i){
      var b = document.createElement("button");
      b.type = "button"; b.className = "opt";
      b.textContent = d.n;
      b.onclick = function(){ set(i); draw(); };
      host.appendChild(b);
      buttons.push({ el:b, val:i });
    });
    groups.push({ buttons:buttons, get:get });
  }

  function sync(){
    /* While a search is running no chip is lit, because the search is
       across all three ranges and a lit chip would claim otherwise. */
    var chips = $("cats"), searching = !!$("q").value.trim();
    if (chips) chips.querySelectorAll("[data-cat]").forEach(function(el){
      el.setAttribute("aria-pressed", !searching && el.dataset.cat === S.cat);
    });
    groups.forEach(function(g){
      var v = g.get();
      g.buttons.forEach(function(b){
        b.el.setAttribute("aria-pressed", b.val === v);
      });
    });
  }

  function build(){
    swatches($("folios"), FOLIOS, function(){return S.folio;}, function(v){S.folio=v;});
    swatches($("inner"),  LACES,  function(){return S.inner;},  function(v){S.inner=v;});
    swatches($("outer1"), LACES,  function(){return S.outer1;}, function(v){S.outer1=v;});
    swatches($("outer2"), LACES,  function(){return S.outer2;}, function(v){S.outer2=v;}, true);
    options($("emboss"), EMBOSS, function(){return S.emboss;}, function(v){S.emboss=v;});
    /* The charm picker is built once and never rebuilt, like the other
       swatch groups — rebuilding on every change throws away whatever the
       keyboard is focused on. Only the chosen list below it is redrawn. */
    var host = $("charmpicker");
    CHARMS.forEach(function(c){
      var el = document.createElement("button");
      el.type = "button"; el.className = "sw";
      el.disabled = !c.ok;
      el.dataset.charm = c.h;
      el.setAttribute("aria-label", "Add " + c.n + " charm");
      el.innerHTML = swatchImg(c) +
        '<span>' + c.n + (c.ok ? "" : ' <small>out of stock</small>') + '</span>';
      el.onclick = function(){ S.charms.push(c.h); draw(); };
      host.appendChild(el);
    });

    $("charmsource").innerHTML = CHARM_SOURCE.shown + " of " + CHARM_SOURCE.total +
      " in " + CHARM_SOURCE.collection + " are published — " + CHARM_SOURCE.hidden +
      ", which the atelier cannot see.";

    options($("press"), PRESS, function(){return S.press;}, function(v){
      S.press = v;
      // trim what is already typed to the new length, and move off a
      // placement the new kind cannot use
      $("initials").value = S.initials = clean(S.initials);
      if (placesFor().indexOf(placement(S.place)) === -1)
        S.place = placesFor()[0].n;
      S.view = "auto";
    });

    /* Range chips. Built once, like every other control on the page —
       rebuilding them would throw away the focus of anyone using a
       keyboard the moment they narrowed the list. */
    var chips = $("cats");
    [{key:"", n:"All"}].concat(CATEGORIES).forEach(function(c){
      var el = document.createElement("button");
      el.type = "button"; el.className = "opt";
      el.dataset.cat = c.key;
      var count = c.key
        ? BOOKS.filter(function(b){ return b.cat === c.key; }).length
        : BOOKS.length;
      el.innerHTML = c.n + ' <span style="opacity:.55">' + count + "</span>";
      el.onclick = function(){
        S.cat = c.key;
        // a chip and a search fighting each other is confusing; the chip wins
        $("q").value = "";
        draw();
      };
      chips.appendChild(el);
    });

    $("chars").textContent = CHARS.split("").join(" ");
    $("exsurcharge").textContent = money(PRESS[1].surcharge);
    placeOptions();
  }

  /* The placement select, rebuilt whenever the press kind changes, since
     the list of places a quote can go is shorter. Rebuilt rather than
     disabled-in-place: a select whose options are mostly greyed out reads
     as broken, where a shorter list reads as a smaller choice. */
  function placeOptions(){
    var sel = $("place"), avail = placesFor();
    sel.innerHTML = "";
    avail.forEach(function(p){
      var o = document.createElement("option");
      o.value = p.n; o.textContent = p.n;
      sel.appendChild(o);
    });
    if (avail.indexOf(placement(S.place)) === -1) S.place = avail[0].n;
    sel.value = S.place;
  }

  // ---- insert list -----------------------------------------------------
  function books(){
    var q = $("q").value.trim().toLowerCase();
    var host = $("inserts");
    host.innerHTML = "";
    /* Grouped by range, because thirty-six titles in one scroll is a
       wall. The chips narrow it to one range; with none chosen the list
       still runs in range order under sticky headings, so the shape of
       the catalogue is visible either way rather than alphabetical mush.

       A search cuts across all three — someone who knows the title they
       want should not have to know which range it is in first. */
    var visible = BOOKS.filter(function(b){
      if (q) return b.t.toLowerCase().indexOf(q) > -1;
      return !S.cat || b.cat === S.cat;
    });

    var groups = [];
    CATEGORIES.forEach(function(c){
      var inCat = visible.filter(function(b){ return b.cat === c.key; });
      if (inCat.length) groups.push({ cat: c, rows: inCat });
    });

    groups.forEach(function(g){
      // one heading only when more than one range is on show — a heading
      // over the only group is a label for something already obvious
      if (groups.length > 1){
        var head = document.createElement("div");
        head.className = "grp";
        head.innerHTML = "<span>" + g.cat.n + "</span><span>" + g.rows.length + "</span>";
        host.appendChild(head);
      }
      g.rows.forEach(function(b){
        var n = 0;
        S.books.forEach(function(t){ if (t === b.t) n++; });
        var on = n > 0;
        // Full is now about the FOLIO, not about this title: once four
        // notebooks are in, no title can be added — including one already
        // chosen, because a fifth of it is still a fifth notebook.
        var full = S.books.length >= MAX_INSERTS;
        var el = document.createElement("button");
        el.type = "button"; el.className = "item";
        el.setAttribute("aria-pressed", on);
        if (full || !b.ok) el.disabled = true;
        el.innerHTML =
          (n > 1 ? '<span class="item__n">' + n + '</span>'
                 : '<span class="item__box"></span>') +
          '<span>' + b.t +
          (!b.ok ? ' <span class="oos">out of stock</span>' : '') +
          '</span><span class="item__p">' + money(b.p) + '</span>';
        el.dataset.title = b.t;
        el.onclick = function(){
          // The row only ever ADDS. Taking one back off is the job of the
          // chosen list below it — the same split as the charms, so a
          // customer who has used step 05 already knows this one.
          if (S.books.length < MAX_INSERTS) S.books.push(b.t);
          draw();
          // this row was destroyed and remade by the redraw above, so put
          // the keyboard back on its replacement rather than on the body
          var again = host.querySelector('[data-title="' + b.t.replace(/"/g,'\\"') + '"]');
          if (again && !again.disabled) again.focus();
        };
        host.appendChild(el);
      });
    });

    if (!visible.length){
      var none = document.createElement("div");
      none.className = "grp";
      none.style.background = "transparent";
      none.style.color = "var(--soft)";
      none.innerHTML = "<span>Nothing matches “" + $("q").value.trim() + "”</span>";
      host.appendChild(none);
    }

    /* Once anything is chosen the chips above name it, with quantities and
       a way to undo — so repeating the titles here put the same list twice,
       a line apart. What is left is the only thing the chips cannot say:
       how full the folio is. */
    $("insertcount").innerHTML = S.books.length
      ? "<b>" + S.books.length + " of " + MAX_INSERTS + "</b> notebook" +
        (MAX_INSERTS === 1 ? "" : "s")
      : "None chosen — <b>0 of " + MAX_INSERTS + "</b>";

    /* What the collection holds versus what the atelier can offer. On the
       page, not in a comment, because the person who can close that gap
       is the one looking at this. */
    $("booksource").innerHTML = BOOK_SOURCE.shown + " of " + BOOK_SOURCE.total +
      " in <b>" + BOOK_SOURCE.collection + "</b> are published — " + BOOK_SOURCE.hidden +
      ", which the atelier cannot see.";
  }

  // ---- the drawing -----------------------------------------------------
  function draw(){
    var f = FOLIOS[S.folio];
    $("body").setAttribute("fill", f.hex);
    $("specline").textContent = f.n;

    // turn the whole object, or don't
    var flat = carried();
    var turn = flat ? CARRY_TURN : 0;
    $("folio").setAttribute("transform",
      "translate(" + COVER.cx + "," + COVER.cy + ") rotate(" + turn + ")");
    // The control says what you are looking at, and pressing it changes
    // that — rather than a label in one place and a verb in another.
    $("turn").textContent = (flat ? "CARRIED FLAT" : "AS IT STANDS") + " ⟳";

    // local -> view, through the same rotation the folio just took
    var rad = turn * Math.PI / 180, cos = Math.cos(rad), sin = Math.sin(rad);
    function toView(x, y){
      return { x: COVER.cx + x * cos - y * sin,
               y: COVER.cy + x * sin + y * cos };
    }

    /* Belly laces. One lace sits ON the centre line — where the elastic
       actually sits on the real folio — and two straddle it. The old
       version pinned lace one above centre whether or not a second one
       joined it, so a single lace always looked slightly fallen. */
    /* LACE THICKNESS, MEASURED.

       On the periwinkle photograph the cord is 11px across a folio 768px
       on its long side — 1.4%, once the shadow it casts on the leather is
       separated from the cord itself. On this cover, whose long side is
       329, that is 5.

       It was 8, which drew the closure as a strap rather than a cord and
       made a two-lace build look like luggage. The off-the-shelf sample
       already had this right, at a hairline. */
    var LACE_W = 5, r1 = $("ribbon1"), r2 = $("ribbon2"), laceY;
    r1.setAttribute("fill", LACES[S.outer1].hex);
    if (S.outer2 === null){
      laceY = -LACE_W / 2;
      r1.setAttribute("y", laceY);
      r2.style.display = "none";
    } else {
      laceY = -LACE_W - 2;
      r1.setAttribute("y", laceY);
      r2.style.display = "";
      r2.setAttribute("y", 2);
      r2.setAttribute("fill", LACES[S.outer2].hex);
    }

    $("innerlace").setAttribute("stroke", LACES[S.inner].hex);

    /* The inserts, drawn A5 and in their own colours.

       48 x 68 is 0.705 — the same ratio as the folio they go into, which
       is the point: four of these fit, and the picture should be able to
       show that rather than assert it. Four sit centred under the folio
       whether one is chosen or four. */
    var pages = $("slots"), fore = $("pages");
    pages.innerHTML = ""; fore.innerHTML = "";
        var n = S.books.length, SW = 48, SH = 68, GAP = 10;
    /* Wide enough for a full row, or for as many as there are if that is
       fewer — so one notebook sits centred rather than hard left of a
       gap the size of three more. */
    var per = Math.min(PLAN.slots.perRow, n) || 1;
    var span = per * SW + (per - 1) * GAP, x0 = PLAN.slots.cx - span / 2;
    var missing = [];
    for (var i = 0; i < n; i++){
      var bk = book(S.books[i]), hex = coverHex(bk);
      var x = x0 + (i % PLAN.slots.perRow) * (SW + GAP);
      var y = PLAN.slots.y + Math.floor(i / PLAN.slots.perRow) * (SH + GAP);
      if (!hex) missing.push(S.books[i]);
      pages.innerHTML +=
        '<rect x="' + x + '" y="' + y + '" width="' + SW + '" height="' + SH + '" rx="1.5" fill="' +
        (hex || HATCH) + '" stroke="var(--ink)" stroke-width="1.1"/>';
      // visible edge on the closed folio — local coords now, so the page
      // block turns with the folio instead of floating beside it
      fore.innerHTML += '<rect x="' + (COVER.w / 2 - 6) + '" y="' + (58 + i * 11) +
        '" width="12" height="8" fill="' + (hex || "var(--panel)") +
        '" stroke="var(--ink)" stroke-width="1"/>';
    }

    /* The inner lace threads the stack, so it runs the height of the
       rows that are actually there — one row or two — rather than a
       length typed into the drawing. In portrait that comes out at the
       396–470 the snippet was authored with, which is the point: one
       rule, and the arrangement decides. */
    var rows = Math.ceil(n / PLAN.slots.perRow) || 1;
    var lace = $("innerlace");
    lace.setAttribute("x1", PLAN.lace.x);
    lace.setAttribute("x2", PLAN.lace.x);
    lace.setAttribute("y1", PLAN.lace.y);
    lace.setAttribute("y2", PLAN.lace.y + rows * SH + (rows - 1) * GAP + 6);

    /* Say out loud which ones have no colour on the product. A hatched
       slot is honest but silent, and the person who can fix it is the
       one reading this page. */
    $("slotnote").textContent = missing.length
      ? "No colour recorded on: " + missing.join(", ")
      : "";

    /* monogram — placed by measurement, not by a lookup table

       The old version carried three hardcoded pixel pairs, so adding a
       placement meant editing the drawing. Now the position comes from
       the placement's own fractions, run through the same rotation as
       the folio, and any placement added to PLACEMENTS just works. */
    var m = $("mono");
    if (S.emboss === 0 || !S.initials){
      m.setAttribute("fill", "none");
      setPressText(m, "");
      m.removeAttribute("transform");
    } else {
      var p = placement(S.place), L = localXY(p), V = toView(L.x, L.y);
      var vx = V.x, vy = V.y;

      // How far the press is from upright IN THIS VIEW. A landscape press
      // seen on a standing folio reads sideways, and showing that is the
      // honest answer to "what will it look like on my desk".
      var lean = (p.reads === "landscape" ? -CARRY_TURN : 0) + turn;

      setPressText(m, S.initials);
      /* Foil gets a hairline of its own shadow behind the glyphs, drawn
         under the fill by paint-order. Gradient alone is not enough:
         somewhere in a five-colour range there is always a cover the
         mid-tones of the gold match, and on that one the press would
         disappear. The stroke is the debossed edge, which is a real
         thing on a pressed foil letter, so it is not a cheat. */
      var foil = S.emboss === 2;
      m.setAttribute("fill", foil ? FOIL : "rgba(0,0,0,.42)");
      m.setAttribute("stroke", foil ? "rgba(0,0,0,.5)" : "none");
      m.setAttribute("stroke-width", foil ? "0.9" : "0");
      m.setAttribute("paint-order", "stroke");
      m.setAttribute("x", 0); m.setAttribute("y", 0);
      m.setAttribute("transform",
        "translate(" + vx.toFixed(1) + "," + vy.toFixed(1) + ") rotate(" + lean + ")");
    }

    /* Charm — tied to the belly lace, and it hangs.

       So rather than a fixed spot on the drawing, take both ends of the
       lace, put them through the same rotation as the folio, and use
       whichever one ends up lower on the page. Turn the folio and the
       charm moves to the other end of the lace by itself, which is what
       a hanging thing does. */
    /* A CLUSTER, NOT A ROW.

       Charms are tied to the lace and they hang, so they gather at
       whichever end of it ends up lower once the folio has turned. They
       are drawn on three cord lengths in rotation and stepped back along
       the lace, which is how a real bunch sits: nothing lines up, and
       nothing sits directly on top of anything else.

       There is no limit. Twelve charms make a dense cluster, which is
       what twelve charms look like — the drawing does not pretend
       otherwise, and it does not silently stop at some number the
       customer was never told about. */
    var cg = $("charms");
    cg.innerHTML = "";
    if (S.charms.length){
      /* They all hang off the SAME point — the lower end of the lace —
         and fan out from it, the way a bunch on a keyring does.

         The first version stepped each charm back ALONG the lace, which
         is fine while the folio stands and the lace is horizontal. Turn
         it and the lace is vertical, so "along" became "upwards" and the
         cluster climbed back into the folio in a single stack. Fanning
         from one anchor has no direction in it, so it is right in both
         views and stays right if a third view is ever added. */
      var mid = laceY + LACE_W / 2;
      var e1 = toView(130, mid), e2 = toView(-130, mid);
      var end = e1.y >= e2.y ? e1 : e2;

      var PER_ROW = 5, SPREAD = 19, ROW_DROP = 23, R = 9;
      var CORD = [20, 31, 25];        // three lengths in rotation
      S.charms.forEach(function(h, i){
        var c = charm(h);
        if (!c) return;
        var col = i % PER_ROW, row = Math.floor(i / PER_ROW);
        var cx = end.x + (col - (PER_ROW - 1) / 2) * SPREAD;
        var cy = end.y + CORD[i % CORD.length] + row * ROW_DROP;
        cg.innerHTML +=
          '<line x1="' + end.x.toFixed(1) + '" y1="' + end.y.toFixed(1) +
          '" x2="' + cx.toFixed(1) + '" y2="' + (cy - R).toFixed(1) +
          '" stroke="var(--ink)" stroke-width="1.1" opacity=".75"/>' +
          '<circle cx="' + cx.toFixed(1) + '" cy="' + cy.toFixed(1) + '" r="' + R +
          '" fill="var(--panel)" stroke="var(--ink)" stroke-width="1.4"/>' +
          // the initial shows through when the product image has not
          // loaded, which is what happens on a page opened from a file
          '<text x="' + cx.toFixed(1) + '" y="' + (cy + 3.5).toFixed(1) +
          '" text-anchor="middle" style="font:8px var(--mono)" fill="var(--soft)">' +
          c.n.charAt(0) + '</text>' +
          '<image href="' + c.img + '" x="' + (cx - R + 1).toFixed(1) +
          '" y="' + (cy - R + 1).toFixed(1) + '" width="' + (2 * R - 2) +
          '" height="' + (2 * R - 2) + '" clip-path="circle(' + (R - 1) +
          'px)" preserveAspectRatio="xMidYMid slice"/>';
      });
    }

    /* The field follows the press kind: how much it will take, what it
       suggests, and how wide the box is. An 18-character quote typed into
       a 118px box scrolls under the cursor and cannot be read back. */
    var pk = press(), inp = $("initials");
    inp.maxLength = pk.max;
    inp.placeholder = pk.hint;
    inp.style.width = isQuote() ? "260px" : "118px";
    $("count").textContent = S.initials
      ? S.initials.length + " of " + pk.max
      : "";
    placeOptions();

    /* Say which way the press reads, in words, next to the choice. The
       diagram shows it, but someone scanning the form should not have to
       infer from a picture that their initials will sit sideways when the
       folio is standing on a desk. */
    var pn = $("placenote"), pp = placement(S.place);
    if (S.emboss === 0){
      pn.textContent = "";
    } else {
      var reads = pp.reads === "landscape"
        ? "Pressed to read with the folio <b>carried flat</b>. Standing on a shelf it sits sideways."
        : "Pressed to read with the folio <b>standing upright</b>.";
      if (isQuote())
        reads += " A quote needs room, so the corners and the spine are not offered for one.";
      pn.innerHTML = reads;
    }

    chosenCharms(); chosenInserts();
    tally(); payload(); books(); sync();
  }

  /* The chosen charms, with a count and a way to take one back off. The
     picker adds; this is where you undo. Keeping the two apart means the
     picker can stay a plain grid of things you can have, rather than
     every tile carrying a stepper it usually does not need. */
  function chosenCharms(){
    var host = $("charmchosen");
    if (!S.charms.length){ host.innerHTML = ""; return; }
    var html = '<p class="sub" style="margin:16px 0 8px">On the lace</p><div class="swatches">';
    charmCounts().forEach(function(g){
      html += '<span class="sw" style="cursor:default">' + swatchImg(g.charm) +
        '<span>' + g.charm.n + (g.qty > 1 ? " ×" + g.qty : "") +
        ' <button type="button" data-drop="' + g.charm.h + '" aria-label="Remove one ' +
        g.charm.n + ' charm" style="font:inherit;background:none;border:0;cursor:pointer;' +
        'color:inherit;padding:0 0 0 8px">✕</button></span></span>';
    });
    html += '</div>';
    host.innerHTML = html;
    host.querySelectorAll("[data-drop]").forEach(function(b){
      b.onclick = function(){
        // last one added of that charm, so repeatedly pressing it counts down
        var i = S.charms.lastIndexOf(b.dataset.drop);
        if (i > -1) S.charms.splice(i, 1);
        draw();
      };
    });
  }

  /* The chosen inserts, with a count and a way to take one back off.
     Deliberately the same markup and the same behaviour as the charms
     above — including counting DOWN from the last one added, so holding
     the ✕ walks a quantity back one at a time. */
  function chosenInserts(){
    var host = $("insertchosen");
    if (!host) return;
    if (!S.books.length){ host.innerHTML = ""; return; }
    var html = '<p class="sub" style="margin:16px 0 8px">In the folio</p><div class="swatches">';
    bookCounts().forEach(function(g){
      var hex = coverHex(g.book);
      html += '<span class="sw" style="cursor:default;--dot:' + (hex || "transparent") + '">' +
        '<i></i><span>' + g.title + (g.qty > 1 ? " ×" + g.qty : "") +
        ' <button type="button" data-drop="' + g.title.replace(/"/g, "&quot;") +
        '" aria-label="Remove one ' + g.title.replace(/"/g, "&quot;") +
        '" style="font:inherit;background:none;border:0;cursor:pointer;' +
        'color:inherit;padding:0 0 0 8px">✕</button></span></span>';
    });
    html += '</div>';
    host.innerHTML = html;
    host.querySelectorAll("[data-drop]").forEach(function(b){
      b.onclick = function(){
        var i = S.books.lastIndexOf(b.dataset.drop);
        if (i > -1) S.books.splice(i, 1);
        draw();
      };
    });
  }

  function lines(){
    var out = [];
    out.push({ label:"Folio — " + FOLIOS[S.folio].n, price:FOLIOS[S.folio].price, real:true });
    out.push({ label:"Inner lace — " + LACES[S.inner].n, price:LACES[S.inner].price, real:false });
    out.push({ label:"Belly lace — " + LACES[S.outer1].n, price:LACES[S.outer1].price, real:false });
    if (S.outer2 !== null)
      out.push({ label:"Belly lace — " + LACES[S.outer2].n, price:LACES[S.outer2].price, real:false });
    if (S.emboss > 0 && S.initials){
      out.push({ label:"Embossing — " + EMBOSS[S.emboss].n, price:EMBOSS[S.emboss].price, real:false });
      /* The long-press charge is its OWN line rather than a bigger
         number on the embossing one. It costs more because it is longer,
         and a customer looking at the panel should be able to see that
         rather than work it out. It also reaches the bench as a fact:
         this build is a quote, not three initials. */
      if (press().surcharge)
        out.push({ label:"Long press — " + press().n.toLowerCase(),
                   price: press().surcharge, real:false });
    }
    /* Grouped for the same reason the charms are: four identical rows
       push the rest of a build off the panel, and "×4" is what the
       customer is actually buying. */
    bookCounts().forEach(function(g){
      var b = g.book;
      if (!b) return;
      out.push({ label:"Insert — " + g.title + (g.qty > 1 ? " ×" + g.qty : ""),
                 price: b.p * g.qty, real:true });
    });
    /* Charms, grouped. Someone who wants three lunar charms gets one
       line reading x3 rather than three identical rows pushing the rest
       of their build off the panel. */
    charmCounts().forEach(function(g){
      out.push({ label:"Charm — " + g.charm.n + (g.qty > 1 ? " ×" + g.qty : ""),
                 price: g.charm.price * g.qty, real:true });
    });
    return out;
  }

  function tally(){
    var L = lines(), total = 0, html = "";
    L.forEach(function(l){
      total += l.price;
      html += '<div class="tally__row"><span>' + l.label + '</span><b>' + money(l.price) + '</b></div>';
    });
    html += '<div class="tally__total"><span>' + L.length + ' lines</span><b>' + money(total) + '</b></div>';
       $("tally").innerHTML = html;
    dockSummary(L.length, total);
  }

  /* The one line the dock shows while it is folded away. Written from the
     same two numbers the tally was just built from, so the bar and the
     list cannot disagree about what the build costs. */
  function dockSummary(n, total){
    var s = $("docksum");
    if (!s) return;
    s.textContent = n + (n === 1 ? " line" : " lines") + " · " + money(total);
  }

  /* ==================================================================
     THE BUILD CODE

     `_atelier` groups the lines of one build. `_spec` is the build
     itself, written once on the folio line, and it is what lets the
     order sheet redraw the diagram months later from the order alone —
     with no cart, no session, and no dependency on this page still
     existing in the form it has today.

     FORMAT. A query string, because that is a format everything already
     knows how to read and a human can check at a glance in the admin:

       v=1;f=PBTF777;il=raven-black;bl=raven-black,saddle-brown;
       e=gold;k=quote;t=BEGIN%20AGAIN%2C%20SOFTL;p=Lower%20centre;
       i=graph-note,cooking-friend-a5-hobby-note;c=charms-lunar*3

     Every VALUE is percent-encoded, which matters more than it looks:
     the press can set `;` `=` `,` and `|`, so a customer is perfectly
     entitled to order a quote containing the very characters holding
     this string together. Encoding the values means they cannot.

     Handles and SKUs, not titles and not variant ids. A title can be
     renamed and a variant id is meaningless to the bench; a handle
     survives both and can be pasted into the admin search.
  ================================================================== */
  function spec(){
    var f = FOLIOS[S.folio], out = [];
    function put(k, v){ if (v !== "" && v != null) out.push(k + "=" + encodeURIComponent(v)); }

    put("v", 1);
    put("f", f.sku);
    put("il", LACES[S.inner].n);
    var belly = [LACES[S.outer1].n];
    if (S.outer2 !== null) belly.push(LACES[S.outer2].n);
    put("bl", belly.join(","));

    if (S.emboss > 0 && S.initials){
      put("e", EMBOSS[S.emboss].n);
      put("k", press().k);
      put("t", S.initials);
      put("p", S.place);
    }
    /* handle*qty, the same grammar the charms already use — so the order
       sheet and the bench sheet, which both already count `*n`, needed no
       change at all to understand four of one notebook. */
    if (S.books.length) put("i", bookCounts().map(function(g){
      var h = g.book ? g.book.h : g.title;
      return h + (g.qty > 1 ? "*" + g.qty : "");
    }).join(","));

    // charms as handle*qty, so twelve of one is four characters, not twelve handles
    if (S.charms.length) put("c", charmCounts().map(function(g){
      return g.charm.h + (g.qty > 1 ? "*" + g.qty : "");
    }).join(","));

    return out.join(";");
  }

  function payload(){
    var id = "atl_" + FOLIOS[S.folio].sku.toLowerCase() + "_" + S.books.length;
    var rows = [];
    rows.push('  { "id": 51539214336175,  "quantity": 1,   // Folio — ' + FOLIOS[S.folio].n);
    rows.push('    "properties": { "_atelier": "' + id + '",');
    rows.push('                    "_spec": "' + spec() + '",');
    rows.push('                    "Configuration": "' + summary() + '" } },');
    rows.push('<span class="miss">  { "id": NEEDS_PRODUCT, "quantity": 1,   // Single lace — ' + LACES[S.inner].n + ' (inner)</span>');
    rows.push('<span class="miss">    "properties": { "_atelier": "' + id + '", "Position": "Inner" } },</span>');
    rows.push('<span class="miss">  { "id": NEEDS_PRODUCT, "quantity": 1,   // Single lace — ' + LACES[S.outer1].n + ' (belly)</span>');
    rows.push('<span class="miss">    "properties": { "_atelier": "' + id + '", "Position": "Belly" } },</span>');
    if (S.outer2 !== null){
      rows.push('<span class="miss">  { "id": NEEDS_PRODUCT, "quantity": 1,   // Single lace — ' + LACES[S.outer2].n + ' (belly)</span>');
      rows.push('<span class="miss">    "properties": { "_atelier": "' + id + '", "Position": "Belly" } },</span>');
    }
    if (S.emboss > 0 && S.initials){
      rows.push('<span class="miss">  { "id": NEEDS_PRODUCT, "quantity": 1,   // Embossing — ' + EMBOSS[S.emboss].n + '</span>');
      rows.push('<span class="miss">    "properties": { "_atelier": "' + id + '", "' +
                (isQuote() ? "Quote" : "Initials") + '": "' + S.initials +
                '", "Placement": "' + S.place + '" } },</span>');
      if (press().surcharge){
        rows.push('<span class="miss">  { "id": NEEDS_PRODUCT, "quantity": 1,   // Long press — ' + press().n.toLowerCase() + '</span>');
        rows.push('<span class="miss">    "properties": { "_atelier": "' + id + '" } },</span>');
      }
    }
    bookCounts().forEach(function(g){
      rows.push('  { "id": LIVE_VARIANT,  "quantity": ' + g.qty + ',   // Insert — ' +
                g.title + (g.qty > 1 ? ' ×' + g.qty : ''));
      rows.push('    "properties": { "_atelier": "' + id + '" } },');
    });
    charmCounts().forEach(function(g){
      rows.push('  { "id": ' + g.charm.id + ', "quantity": ' + g.qty +
                ',   // Charm — ' + g.charm.n + (g.qty > 1 ? ' ×' + g.qty : ''));
      rows.push('    "properties": { "_atelier": "' + id + '" } },');
    });
    var last = rows.length - 1;
    rows[last] = rows[last].replace(/,(<\/span>)?$/, "$1");
    if ($("json")) $("json").innerHTML = '{ "items": [\n' + rows.join("\n") + '\n] }';
  }

  function summary(){
    var p = [FOLIOS[S.folio].n + " folio",
             "inner " + LACES[S.inner].n.toLowerCase(),
             "belly " + LACES[S.outer1].n.toLowerCase()];
    if (S.outer2 !== null) p.push("+ " + LACES[S.outer2].n.toLowerCase());
    if (S.emboss > 0 && S.initials)
      p.push('"' + S.initials + '" ' + EMBOSS[S.emboss].n.toLowerCase() +
             " " + press().n.toLowerCase() + ", " + S.place.toLowerCase());
    if (S.books.length) p.push(S.books.length + " insert" + (S.books.length > 1 ? "s" : ""));
    if (S.charms.length)
      p.push(charmCounts().map(function(g){
        return (g.qty > 1 ? g.qty + " " : "") + g.charm.n.toLowerCase();
      }).join(" + ") + " charm" + (S.charms.length > 1 ? "s" : ""));
    return p.join(" · ");
  }

  /* draw(), not books(). Typing in the search has to reach sync() as
     well, because a search runs across all three ranges and the chip
     that was lit before must go dark. Calling books() alone repainted
     the list and left the chip claiming a filter that was no longer
     being applied. */
  $("q").addEventListener("input", draw);
  $("initials").addEventListener("input", function(){
    /* Filter on the way in rather than on the way out. A character the
       press cannot set is rejected where the customer can see it happen,
       not quietly dropped somewhere between here and the order. */
    var at = this.selectionStart, before = this.value;
    S.initials = clean(before);
    if (this.value !== S.initials){
      this.value = S.initials;
      this.setSelectionRange(at - (before.length - S.initials.length), at - (before.length - S.initials.length));
    }
    draw();
  });

  // Choosing a placement hands the view back to `auto`, so the folio
  // turns to whichever way that press reads. Turning it by hand after
  // that is a deliberate override and is left alone until they choose
  // a different placement.
  $("place").addEventListener("change", function(){
    S.place = this.value; S.view = "auto"; draw();
  });
  $("turn").addEventListener("click", function(){
    S.view = carried() ? "portrait" : "carried"; draw();
  });
  /* ---- ADD TO CART ---------------------------------------------------

     One request with every line in it. `/cart/add.js` takes an `items`
     array and adds them atomically: if any one line fails — a lace that
     sold out while the customer was choosing charms — NOTHING is added.
     That matters more here than anywhere else on the store, because half
     an atelier build is not a smaller order, it is a broken one.

     Every line carries the `_atelier` token so the cart can group them
     and the order sheet can find them. Only the folio carries `_spec`,
     which is the build itself and what the bench redraws from.
  --------------------------------------------------------------------- */
  function cartLines(){
    var token = "atl_" + (FOLIOS[S.folio].sku || "folio").toLowerCase() + "_" +
                Math.random().toString(36).slice(2, 8);
    var tie = { _atelier: token };
    var out = [];

    function line(id, qty, props){
      var p = { _atelier: token };
      for (var k in props) if (props.hasOwnProperty(k)) p[k] = props[k];
      out.push({ id: id, quantity: qty, properties: p });
    }

    line(FOLIOS[S.folio].id, 1, { _spec: spec(), "Configuration": summary() });
    line(LACES[S.inner].id, 1, { "Position": "Inner" });
    line(LACES[S.outer1].id, 1, { "Position": "Belly" });
    if (S.outer2 !== null) line(LACES[S.outer2].id, 1, { "Position": "Belly" });

    if (S.emboss > 0 && S.initials){
      var p = { "Placement": S.place };
      p[isQuote() ? "Quote" : "Initials"] = S.initials;
      line(EMBOSS[S.emboss].id, 1, p);
      if (press().surcharge && DATA.longPressId)
        line(DATA.longPressId, 1, {});
    }

    /* One line of quantity n, not n lines of one. Shopify would merge
       them anyway — identical variant, identical properties — and it is
       better to send what is meant than to rely on that. */
    bookCounts().forEach(function(g){
      if (g.book) line(g.book.id, g.qty, { "Insert": g.book.t });
    });
    charmCounts().forEach(function(g){ line(g.charm.id, g.qty, {}); });

    return out;
  }

  var addBtn = $("add");
  addBtn.addEventListener("click", function(){
    var btn = this;
    if (btn.disabled) return;

    // refuse rather than half-build
    if (S.emboss > 0 && !S.initials){
      say("Type the initials or quote to press, or set the finish to None.");
      return;
    }
    /* `cartLines` drops any line whose variant id is missing — a
       component that has been unpublished since the page loaded. Count
       what it dropped rather than discovering it at checkout. */
    var wanted = cartLines();
    var lines = wanted.filter(function(l){ return l.id; });
    if (lines.length !== wanted.length){
      say("Part of this build is no longer available. Reload the page to see what changed.");
      return;
    }
    if (!lines.length){ say("Nothing to add yet."); return; }

    btn.disabled = true;
    var was = btn.textContent;
    btn.textContent = "Adding…";

    fetch((window.Shopify && Shopify.routes && Shopify.routes.root || "/") + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ items: lines })
    }).then(function(r){
      return r.json().then(function(body){ return { ok: r.ok, body: body }; });
    }).then(function(res){
      btn.disabled = false;
      btn.textContent = was;
      if (!res.ok){
        /* Shopify names the line it could not add. Saying which one is
           the difference between the customer changing that choice and
           the customer giving up. */
        say(res.body && res.body.description
              ? res.body.description
              : "Something in this build is no longer available.");
        return;
      }
      say("");
      document.dispatchEvent(new CustomEvent("theme:cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:open"));
    }).catch(function(){
      btn.disabled = false;
      btn.textContent = was;
      say("Could not reach the cart. Try again in a moment.");
    });
  });

  function say(msg){
    var el = $("adderr");
    if (!el) return;
    el.textContent = msg || "";
    el.hidden = !msg;
  }
  /* ==================================================================
     THE MOBILE DOCK

     Under 900px the tally, the error line and the Add to cart button are
     MOVED — not copied — into the bar at the foot of the screen, and
     moved back if the window widens. Moved, because a second copy of the
     button would need a second copy of the handler, the disabled state
     and the "Adding…" label, and the day they drift is the day someone
     is charged twice.

     A comment node is left where each one lived. Restoring by remembered
     nextSibling looks equivalent and is not: the error line's next
     sibling IS the button, and if the button has not been put back yet
     the browser throws. A marker cannot go stale.

     Everything here is guarded on the elements existing, so a section
     rendered before this markup shipped simply does nothing.
  ================================================================== */
  function dockInit(){
    var dockEl = $("dock"), body = $("dockbody"),
        errSlot = $("dockerr"), ctaSlot = $("dockcta"), toggle = $("docktoggle");
    if (!dockEl || !body || !ctaSlot || !toggle) return;

    var mq = window.matchMedia("(max-width:900px)");
    var marks = null;

    function take(el, slot){
      if (!el) return null;
      var mark = document.createComment("atl-home");
      el.parentNode.insertBefore(mark, el);
      slot.appendChild(el);
      return mark;
    }
    function give(el, mark){
      if (!el || !mark || !mark.parentNode) return;
      mark.parentNode.insertBefore(el, mark);
      mark.parentNode.removeChild(mark);
    }

    function open(v){
      toggle.setAttribute("aria-expanded", v ? "true" : "false");
      body.hidden = !v;
    }

    /* The page is padded by the height of the BAR, not of the open list.
       Padding for the list as well would leave a hole under the content
       when it is folded, and expanding would jump the page. */
    function measure(){
      var bar = dockEl.querySelector(".dock__bar");
      if (bar) host.style.setProperty("--dock-h", bar.offsetHeight + "px");
    }

    function dockOn(){
      if (marks) return;
      marks = {
        tally: take($("tally"), body),
        err:   take($("adderr"), errSlot),
        add:   take($("add"), ctaSlot)
      };
      host.dataset.dock = "on";
      open(false);
      measure();
    }
    function dockOff(){
      if (!marks) return;
      give($("add"), marks.add);
      give($("adderr"), marks.err);
      give($("tally"), marks.tally);
      marks = null;
      delete host.dataset.dock;
      open(false);
      host.style.removeProperty("--dock-h");
    }

    toggle.addEventListener("click", function(){ open(body.hidden); });

    /* The dock and the drawing's arrangement answer to the same
       breakpoint, so they cannot end up disagreeing about whether
       this is a phone. */
    function apply(){
      if (mq.matches){ dockOn();  applyLayout("landscape"); }
      else           { dockOff(); applyLayout("portrait");  }
    }
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else if (mq.addListener) mq.addListener(apply);
    window.addEventListener("resize", function(){ if (marks) measure(); });

    apply();
  }

  build();
  draw();
  dockInit();
}

/* One init per section on the page, and never twice.

   The theme editor re-renders a section without reloading the page, so
   `shopify:section:load` matters as much as DOMContentLoaded — without
   it the atelier goes dead the first time someone drags it in the
   customizer, which is exactly when Alanah would be looking at it. */
function atelierBoot(scope){
  (scope || document).querySelectorAll("[data-atelier]").forEach(function(host){
    if (host.dataset.atelierReady) return;
    host.dataset.atelierReady = "1";
    try { atelierInit(host); }
    catch (e) { if (window.console) console.error("atelier:", e); }
  });
}
if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", function(){ atelierBoot(); });
else atelierBoot();
document.addEventListener("shopify:section:load", function(e){ atelierBoot(e.target); });